import { platformAuthenticatorIsAvailable, startAuthentication, startRegistration } from "@simplewebauthn/browser";
import _ from "lodash";
import { call, put, takeEvery } from 'redux-saga/effects';
import Cookies from "universal-cookie";
import { appLog, logAppsflyerAction, setIsOpenRespawnBannerVisibleAction } from "../app/appActions.js";
import { LOG_AF_EVENT_NAMES, LOG_EVENT_TYPES } from "../app/appConstants.js";
import { appHistoryPushWithSearchParams } from "../app/appHistory.js";
import { appInitStartSaga } from "../app/appSagas";
import { failurePut } from "../app/failurePut.js";
import { getRequest, postRequest } from "../app/request.js";
import { isClientAndroid, isClientAndroidNative } from "../components/ClientInfo/ClientInfo";
import {
    registrationFailure,
    registrationSuccess,
    setChangeEmailSuccess,
    setRegistrationDataAction
} from "../modules/Login/loginActions.js";
import { updateCookiesOnAuthSaga, validateRegisterDataSaga } from "../modules/Login/loginSagas";
import { MODALS_LOG_ACTION_TYPES } from "../modules/Modals/Modals.jsx";
import { isEmailValid } from "../utils/escapeUtils";
import { MOBILE_SLIDER_PAGES_IDS } from "./components/MobileSliderPages/mobileSliderPagesConstants";
import {
    authRegisterMobileAppFailure,
    authRegisterMobileAppSuccess,
    authVerifyMobileAppFailure,
    authVerifyMobileAppSuccess,
    openMobileSliderPageAction,
} from "./mobileAppActions";
import {
    AUTH_REGISTER_MOBILE_APP_ACTION,
    AUTH_VERIFY_MOBILE_APP_ACTION,
    CLOSE_MOBILE_SLIDER_PAGE,
    OPEN_MOBILE_SLIDER_PAGE,
    REGISTRATION_PREPARE_MOBILE_ACTION,
    REGISTRATION_START_MOBILE_ACTION,
} from "./mobileAppConstants";

const browserCookies = new Cookies();

function* openMobileSliderPageSaga({ payload }) {
    const { name, ...searchParams } = _.isObject(payload) ? payload : { name: payload };
    appHistoryPushWithSearchParams({
        s: name,
        ...searchParams,
    });
    yield put(appLog({
        type: LOG_EVENT_TYPES.page,
        payload: {
            action: MODALS_LOG_ACTION_TYPES.OPEN,
            name,
            ...searchParams,
        }
    }));
}

function* closeMobileSliderPageSaga() {
    const urlParams = new URLSearchParams(window.location.search);
    const currentSliderName = urlParams.get('s');
    appHistoryPushWithSearchParams({
        s: null,
    });
    yield put(appLog({
        type: LOG_EVENT_TYPES.slider,
        payload: {
            action: 'close',
            name: currentSliderName,
        }
    }));
}

export function* registrationPrepareMobileSaga({ payload }) {
    try {
        yield call(validateRegisterDataSaga, { payload });
        yield put(setRegistrationDataAction(payload));
        yield call(postRequest, '/user/register-check', payload);
        yield put(openMobileSliderPageAction(MOBILE_SLIDER_PAGES_IDS.REGISTER_EMAIL_CHECK));
        yield put(logAppsflyerAction({
             name: LOG_AF_EVENT_NAMES.SIGN_UP_FORM_FILLED,
             payload: { phone_filled: !!payload.phone },
        }));
    } catch (e) {
        yield put(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.SIGN_UP_ERROR,
            payload: {
                error_code: e.response?.data?.messageCode || e.messageCode || 'error.something_went_wrong'
            },
        }))
        yield call(failurePut, e, registrationFailure);
    }
}


export function* registrationStartMobileSaga({ payload }) {
    try {
        if (payload.country) {
            browserCookies.set('country', payload.country, { path: '/', maxAge: 60 * 60 * 24 * 365 });
        }
        if (!isEmailValid(payload.email)) {
            throw { messageCode: 'error.email_not_valid' };
        }
        const locale = browserCookies.get('locale');
        const data = yield call(postRequest, '/user/register', {
            email: payload.email,
            phone: payload.phone,
            lang: locale,
            country: payload.country,
            password: payload.password,
        });
        yield put(registrationSuccess(payload));
        yield put(setChangeEmailSuccess(false));
        yield call(updateCookiesOnAuthSaga, { payload: { token: data.token, secret: data.secret } });
        yield call(appInitStartSaga);
        yield put(logAppsflyerAction({ name: LOG_AF_EVENT_NAMES.SIGN_UP_COMPLETED }));
        if (isClientAndroid && !isClientAndroidNative) {
            yield put(openMobileSliderPageAction(MOBILE_SLIDER_PAGES_IDS.OPEN_MOBILE_APP));
        }
        yield put(setIsOpenRespawnBannerVisibleAction(true));
    } catch (e) {
        yield call(failurePut, e, registrationFailure);
        yield put(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.SIGN_UP_ERROR,
            payload: {
                error_code: e.response?.data?.messageCode || e.messageCode ||'error.something_went_wrong'
            },
        }));
    }
}

export function* authRegisterMobileAppSaga({ payload }) {
    try {
        browserCookies.remove('credential_id');
        localStorage.removeItem('credentialId');
        const options = yield call(getRequest, '/app/auth/registration/options');
        const resp = yield call(startRegistration, options);
        browserCookies.set('credential_id', resp.id, { path: '/', maxAge: 60 * 60 * 24 * 365 });
        yield call(postRequest, '/app/auth/registration/verify', resp);
        yield put(authRegisterMobileAppSuccess());
        payload.onSuccessCallback && payload.onSuccessCallback();
    } catch (e) {
        yield call(failurePut, e, authRegisterMobileAppFailure);
        payload.onFailureCallback && payload.onFailureCallback();
    }
}

export function* authVerifyMobileAppSaga({ payload }) {
    const pin = browserCookies.get('pin');
    try {
        const isAuthenticatorAvailable = yield call(platformAuthenticatorIsAvailable);
        const token = browserCookies.get('token');
        const credentialId = browserCookies.get('credential_id');

        if (token && credentialId && isAuthenticatorAvailable) {
            const options = yield call(getRequest, '/app/auth/authentication/options');
            const resp = yield call(startAuthentication, options);
            const verification = yield call(postRequest, '/app/auth/authentication/verify', resp);
            if (verification.verified) {
                browserCookies.set('pinOk', pin, {path: '/', maxAge: 60 * 5});
                yield put(logAppsflyerAction({
                    name: LOG_AF_EVENT_NAMES.LOGIN_COMPLETED,
                    payload: { method: 'fingerprint' },
                }))
            }
        }
        yield put(authVerifyMobileAppSuccess());
    } catch (e) {
        yield call(failurePut, e, authVerifyMobileAppFailure);
    } finally {
        payload.onFinallyCallback && payload.onFinallyCallback();
    }
}

export function* mobileAppSagas() {
    yield takeEvery(OPEN_MOBILE_SLIDER_PAGE, openMobileSliderPageSaga);
    yield takeEvery(CLOSE_MOBILE_SLIDER_PAGE, closeMobileSliderPageSaga);
    yield takeEvery(REGISTRATION_PREPARE_MOBILE_ACTION, registrationPrepareMobileSaga);
    yield takeEvery(REGISTRATION_START_MOBILE_ACTION, registrationStartMobileSaga);
    yield takeEvery(AUTH_REGISTER_MOBILE_APP_ACTION, authRegisterMobileAppSaga);
    yield takeEvery(AUTH_VERIFY_MOBILE_APP_ACTION, authVerifyMobileAppSaga);
}
